import { useEffect, useState } from "react";
import { it } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const toIT = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || "") ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseIT = (t) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t.trim());
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1]);
  return d.getDate() === +m[1] && d.getMonth() === +m[2] - 1 ? toISO(d) : null;
};
const mask = (t) => {
  const n = t.replace(/\D/g, "").slice(0, 8);
  return [n.slice(0, 2), n.slice(2, 4), n.slice(4)].filter(Boolean).join("/");
};

// Data in formato gg/mm/aaaa con calendario italiano; value/onChange in ISO (aaaa-mm-gg)
export const DateInputIT = ({ value, onChange, className = "", testid, disabled = false, title }) => {
  const [txt, setTxt] = useState(toIT(value));
  const [open, setOpen] = useState(false);
  const [bad, setBad] = useState(false);
  useEffect(() => { setTxt(toIT(value)); setBad(false); }, [value]);
  const type = (e) => {
    const t = mask(e.target.value); setTxt(t);
    if (!t) { setBad(false); onChange(""); }
    else { const iso = parseIT(t); setBad(t.length === 10 && !iso); if (iso) onChange(iso); }
  };
  const blur = () => {
    if (txt && !parseIT(txt)) { toast.error(`Data non valida: ${txt}`); }
    setBad(false); setTxt(toIT(value));
  };
  const sel = value && /^\d{4}-\d{2}-\d{2}/.test(value) ? new Date(+value.slice(0, 4), +value.slice(5, 7) - 1, +value.slice(8, 10)) : undefined;
  return (
    <div className={`relative ${className}`}>
      <Input value={txt} onChange={type} onBlur={blur} placeholder="gg/mm/aaaa" inputMode="numeric" disabled={disabled} title={title}
        className={`pr-9 bg-white ${bad ? "border-red-400 focus-visible:ring-red-300" : ""} ${/\bh-8\b/.test(className) ? "h-8 text-sm" : ""}`} data-testid={testid} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid={testid ? `${testid}-cal` : undefined} aria-label="Apri calendario">
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" locale={it} weekStartsOn={1} selected={sel} defaultMonth={sel}
            onSelect={(d) => { onChange(d ? toISO(d) : ""); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Data e ora: data gg/mm/aaaa + ora; value/onChange in formato "aaaa-mm-ggTHH:MM"
export const DateTimeInputIT = ({ value, onChange, className = "", testid }) => {
  const [d, t] = (value || "").split("T");
  const emit = (nd, nt) => onChange(nd ? `${nd}T${nt || "09:00"}` : "");
  return (
    <div className={`flex gap-2 ${className}`}>
      <DateInputIT value={d || ""} onChange={(v) => emit(v, t)} className={`flex-1 ${/\bh-8\b/.test(className) ? "h-8" : ""}`} testid={testid} />
      <Input type="time" value={(t || "").slice(0, 5)} onChange={(e) => emit(d, e.target.value)} className={`w-28 bg-white ${/\bh-8\b/.test(className) ? "h-8 text-sm" : ""}`} data-testid={testid ? `${testid}-ora` : undefined} />
    </div>
  );
};
